import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { DraftsList } from './DraftsList';
import { useDraftsList } from '../../hooks/useDraftsList';

interface DraftsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDraft: (draftId: string) => void;
  hasUnsavedChanges?: boolean;
}

/**
 * Modal for displaying and managing user drafts
 */
export const DraftsModal: React.FC<DraftsModalProps> = ({
  isOpen,
  onClose,
  onSelectDraft,
  hasUnsavedChanges = false,
}) => {
  const { drafts, loading, error, deleteDraft, refetch } = useDraftsList({
    autoFetch: isOpen,
  });
  
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingDraftId, setPendingDraftId] = useState<string | null>(null);

  const handleSelect = useCallback((draftId: string) => {
    if (hasUnsavedChanges) {
      setPendingDraftId(draftId);
      setShowUnsavedWarning(true);
    } else {
      onSelectDraft(draftId);
    }
  }, [hasUnsavedChanges, onSelectDraft]);

  const handleConfirmSelect = useCallback(() => {
    if (pendingDraftId) {
      onSelectDraft(pendingDraftId);
      setPendingDraftId(null);
      setShowUnsavedWarning(false);
    }
  }, [pendingDraftId, onSelectDraft]);

  const handleCancelSelect = useCallback(() => {
    setPendingDraftId(null);
    setShowUnsavedWarning(false);
  }, []);

  const handleDeleteRequest = useCallback((draftId: string) => {
    setDeleteConfirmId(draftId);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteConfirmId) {
      await deleteDraft(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  }, [deleteConfirmId, deleteDraft]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Your Drafts</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          <DraftsList
            drafts={drafts}
            loading={loading}
            error={error}
            onSelect={handleSelect}
            onDelete={handleDeleteRequest}
            onRetry={refetch}
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Click on a draft to continue editing
          </p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={handleCancelDelete} />
          <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Draft?</h3>
            <p className="text-gray-600 mb-4">
              This action cannot be undone. Are you sure you want to delete this draft?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={handleCancelSelect} />
          <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unsaved Changes</h3>
            <p className="text-gray-600 mb-4">
              You have unsaved changes. Loading a draft will discard your current progress.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelSelect}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={handleConfirmSelect}
                className="px-4 py-2 text-sm font-medium text-white bg-pink-500 hover:bg-pink-600 rounded-md transition-colors"
              >
                Load Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Wrap in `.be-embedded-app` so the map embed's scoped Tailwind CSS reaches
  // portaled content rendered at document.body. Outside the embed (no map.css)
  // the class is inert.
  return ReactDOM.createPortal(
    <div className="be-embedded-app">{modalContent}</div>,
    document.body
  );
};
